import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Loader2, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";

import { changeBranch, changeCompany } from "../../redux/actions/authAction";
import { getDate } from "../../functions/dateFunctions";
import { postRequest } from "../../services/mainApp.service";

import { FilterBar } from "../GraphicalDashboardTwo/components/FilterBar";
import { KPICards } from "../GraphicalDashboardTwo/components/KPICards";
import { TopProductsSection } from "../GraphicalDashboardTwo/components/TopProductsSection";
import { TopCustomersSection } from "../GraphicalDashboardTwo/components/TopCustomersSection";
import { CategoriesChart } from "../GraphicalDashboardTwo/components/CategoriesChart";
import { AreaSalesChart } from "../GraphicalDashboardTwo/components/AreaSalesChart";
import "../GraphicalDashboardTwo/style.css";

const COLORS = ["#01796d", "#3b82f6", "#a855f7", "#f97316", "#ec4899", "#10b981", "#f59e0b"];

function SimpleBarChart({ title, subtitle, data, nameKey, valueKey }) {
  const processedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data
      .map((item) => ({
        name: item[nameKey] || item[nameKey?.toLowerCase?.()] || item[Object.keys(item)[0]] || "Unknown",
        value: Number(item[valueKey]) || 0,
      }))
      .filter((item) => item.value > 0);
  }, [data, nameKey, valueKey]);

  return (
    <div className="bg-white rounded-2xl p-5 shadow-lg border border-slate-200 hover:shadow-xl transition-all">
      <div className="flex items-center justify-between mb-5 gap-3">
        <div>
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
        </div>
        <div className="bg-gradient-to-br from-[#01796d] to-teal-600 p-3 rounded-xl shadow-lg">
          <TrendingUp className="w-5 h-5 text-white" />
        </div>
      </div>

      {processedData.length === 0 ? (
        <div className="h-[260px] flex items-center justify-center text-slate-400">
          No data available
        </div>
      ) : (
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={processedData} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fill: "#64748b", fontSize: 12 }}
                axisLine={{ stroke: "#e2e8f0" }}
                interval={0}
                angle={-30}
                textAnchor="end"
                height={55}
              />
              <YAxis tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
                }}
                formatter={(value) => [value, "Count"]}
              />
              <Bar dataKey="value" radius={[16, 16, 0, 0]}>
                {processedData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

const Dashboard = () => {
  const dispatch = useDispatch();
  const appStore = useSelector((state) => state.authReducer) || {};
  const { RoleId, dateFrom, dateTo, branchId, RoleName, companyList, CompanyId } = appStore;
  const { RefreshStats } = useSelector((state) => state.RefreshReducer) || {};
  const [dashboardData, setDashboardData] = useState({});
  const [reportData, setReportData] = useState({});
  const [branchDropdownList, setBranchDropdownList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previousSummary, setPreviousSummary] = useState(null);
  const [dateRange, setDateRange] = useState({
    type: "month",
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    endDate: new Date(),
  });
  const [orderTypeFilter, setOrderTypeFilter] = useState("all");
  const [viewMode, setViewMode] = useState("day");
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");

  // useEffect(() => {
  //   if (RoleId !== 14 && !selectedBranchId && appStore?.userBranchList?.length > 0) {
  //     setSelectedBranchId(appStore.userBranchList[0]?.BranchId || null);
  //   }
  // }, [RoleId, appStore.userBranchList, selectedBranchId]);

  const branchOptions = RoleId === 14 ? branchDropdownList : appStore?.userBranchList || [];

  const getRangeStrings = () => {
    if (RoleId === 14) {
      return {
        DateFrom: dateFrom || getDate(Date.now() - 30 * 24 * 60 * 60 * 1000),
        DateTo: dateTo || getDate(),
      };
    }

    return {
      DateFrom: dateRange?.startDate
        ? format(dateRange.startDate, "yyyy-MM-dd")
        : format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"),
      DateTo: dateRange?.endDate
        ? format(dateRange.endDate, "yyyy-MM-dd")
        : format(new Date(), "yyyy-MM-dd"),
    };
  };

  const getReport = async () => {
    setLoading(true);
    try {
      const range = getRangeStrings();
      const branchValue =
        RoleId === 14
          ? branchId
          : selectedBranchId || null;

      const selectedCompanyParam = selectedCompanyId ? Number(selectedCompanyId) : appStore.CompanyId;
      const response = await postRequest("DashboardReport", {
        OperationId: 1,
        CompanyId: selectedCompanyParam,
        BranchId: branchValue,
        ...range,
      });

      const dataSet = response?.data?.DataSet || {};
      setDashboardData(dataSet);
      setReportData(dataSet.Table?.[0] || {});
      setBranchDropdownList(dataSet.Table11 || dataSet.Table7 || []);
      setPreviousSummary(dataSet.Table?.[0] || null);
    } catch (error) {
      console.error("Dashboard report error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getReport();
  }, [selectedCompanyId]);

  useEffect(() => {
    if (RefreshStats === 1) {
      getReport();
      dispatch({ type: "REFRESH_STATS", payload: 0 });
    }
  }, [RefreshStats]);

  const graphData = useMemo(() => ({
    summary: reportData,
    orderByStatus: dashboardData?.Table1 || [],
    newReturning: dashboardData?.Table6 || [],
    paymentType: dashboardData?.Table8 || [],
    orderBySource: dashboardData?.Table3 || [],
    orderByGender: dashboardData?.Table7 || [],
    topProducts: dashboardData?.Table2 || [],
    topCategories: dashboardData?.Table9 || [],
    topCustomers: dashboardData?.Table10 || [],
    areaSales: dashboardData?.Table4 || [],
  }), [dashboardData, reportData]);

  const filteredData = useMemo(() => {
    if (orderTypeFilter === "all") return graphData;
    const search = orderTypeFilter.toLowerCase();
    return {
      ...graphData,
      orderByStatus: graphData.orderByStatus.filter((item) =>
        (item.OrderStatus || "").toString().toLowerCase().includes(search)
      ),
      newReturning: graphData.newReturning.filter((item) =>
        (item.CustomerType || "").toString().toLowerCase().includes(search)
      ),
      orderBySource: graphData.orderBySource.filter((item) =>
        (item.OrderSource || "").toString().toLowerCase().includes(search)
      ),
      orderByGender: graphData.orderByGender.filter((item) =>
        (item.Title || "").toString().toLowerCase().includes(search)
      ),
      paymentType: graphData.paymentType.filter((item) =>
        (item.PaymentMode || "").toString().toLowerCase().includes(search)
      ),
    };
  }, [graphData, orderTypeFilter]);

  const handleBranchChange = (value) => {
    setSelectedBranchId(value);
    if (RoleId === 14) {
      dispatch(changeBranch(value));
    }
  };

  return (
    <div className="gd2 min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      {loading && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 shadow-2xl flex items-center gap-3">
            <Loader2 className="animate-spin text-primary" />
            <span className="text-slate-700 font-medium">Loading dashboard data...</span>
          </div>
        </div>
      )}

      <div className="max-w-[1920px] mx-auto px-2 sm:px-6 lg:px-8 space-y-6 py-6">
        <FilterBar
          dateRange={dateRange}
          setDateRange={setDateRange}
          orderTypeFilter={orderTypeFilter}
          setOrderTypeFilter={setOrderTypeFilter}
          viewMode={viewMode}
          setViewMode={setViewMode}
          onRefresh={getReport}
          companyOptions={companyList || []}
          selectedCompanyId={selectedCompanyId}
          onCompanyChange={(value) => {
            setSelectedCompanyId(value || "");
            if (value) {
              const selectedCompany = companyList.find(
                (c) => String(c.CompanyId) === String(value)
              );
              if (selectedCompany) {
                if (selectedCompany?.WebsiteUrl) {
                  localStorage.setItem("WebsiteUrl", selectedCompany.WebsiteUrl);
                }
                dispatch(changeCompany(selectedCompany));
              }
            }
          }}
          dashboardData={filteredData}
          showCompanySelect={RoleId === 14 && RoleName === "Management"}
          branchOptions={branchOptions}
          selectedBranchId={selectedBranchId}
          setSelectedBranchId={handleBranchChange}
        />

        <KPICards data={filteredData} previousSummary={previousSummary} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SimpleBarChart
            title="Order By Status"
            subtitle="Order count by status"
            data={filteredData.orderByStatus}
            nameKey="OrderStatus"
            valueKey="OrderCount"
          />
          <SimpleBarChart
            title="New vs Returning Customers"
            subtitle="Customer type distribution"
            data={filteredData.newReturning}
            nameKey="CustomerType"
            valueKey="OrderCount"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <SimpleBarChart
            title="Payment Type"
            subtitle="Orders by payment mode"
            data={filteredData.paymentType}
            nameKey="PaymentMode"
            valueKey="OrderCount"
          />
          <SimpleBarChart
            title="Order By Source"
            subtitle="Orders by source"
            data={filteredData.orderBySource}
            nameKey="OrderSource"
            valueKey="OrderCount"
          />
          <SimpleBarChart
            title="Order By Gender"
            subtitle="Orders by customer gender"
            data={filteredData.orderByGender}
            nameKey="Title"
            valueKey="OrderCount"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
          <CategoriesChart data={filteredData.topCategories} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TopProductsSection data={filteredData.topProducts} />
          <TopCustomersSection data={filteredData.topCustomers} />
        </div>

        <AreaSalesChart data={filteredData.areaSales} />
      </div>
    </div>
  );
};

export default Dashboard;
